/** 投放项目里的长原因：默认只显示一行结论，点「详情」原位展开全文，避免说明文字把表格列和进度卡撑高。 */
import { useId, useState } from 'react';

const ITEM_LABEL = /违规(?:词|句)第 \d+ 项/gu;
// 服务端采用结论只有这两种开头（landing-chapter-adoption-validation.ts）；拒用说明是运营自由文本，
// 末段出现「采用」二字也不是结论。
const ADOPTION_CONCLUSION = /^(?:已自动采用|已保存候选)/u;
// 短于此长度的单段原因一行放得下，直接显示，不出「详情」。
const SHORT_TEXT = 40;
// 一行摘要：在可用宽度内换行后截断，不靠 nowrap 把表格列撑宽。
export const landingOneLineStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;

/**
 * 采用原因 / 抽查提示是「各项原因；…；采用结论」体例：取最后一段结论并附违规词 / 句项数。
 * 其余文本（执行失败、拒用说明）原因在前，原样返回，由一行截断保留开头的原因。
 */
export function landingReasonSummary(text: string): string {
  const parts = text
    .split('；')
    .map((part) => part.trim())
    .filter(Boolean);
  const conclusion = parts.at(-1);
  if (!conclusion || parts.length <= 1 || !ADOPTION_CONCLUSION.test(conclusion)) return text.trim();
  const items = new Set(text.match(ITEM_LABEL) ?? []).size;
  if (!items) return conclusion;
  // v2 候选原因只列前 3 项、不附「另有 N 项」，此时项数只是下界。
  const lowerBound = conclusion.includes('未自动采用') && items >= 3 && !/另有 \d+ 项/u.test(text);
  return `${conclusion}（${lowerBound ? `至少 ${items}` : items} 项）`;
}

export function LandingCollapsibleNote({
  text,
  summary = landingReasonSummary(text),
  tone = 'muted',
  label,
}: {
  text: string;
  summary?: string;
  /** muted：提示类；neg：终态失败，文字保留红字与 role=alert。 */
  tone?: 'muted' | 'neg';
  /** 按钮的上下文名称（如「第 3 章未采用原因」），表格多行时读屏可区分。 */
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const textId = useId();
  const collapsible = summary !== text || text.length > SHORT_TEXT;
  return (
    <div className="hstack" style={{ gap: 4, minWidth: 0, alignItems: 'flex-start' }}>
      <span
        id={textId}
        {...(tone === 'neg' ? { role: 'alert' } : {})}
        className={tone === 'muted' ? 'muted' : undefined}
        style={{
          ...(tone === 'neg' ? { color: 'var(--neg)' } : {}),
          overflowWrap: 'anywhere',
          ...(collapsible && !open ? landingOneLineStyle : {}),
        }}
        title={collapsible && !open ? text : undefined}
      >
        {open || !collapsible ? text : summary}
      </span>
      {collapsible && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ flexShrink: 0 }}
          aria-expanded={open}
          aria-controls={textId}
          // 名称以可见文字开头（WCAG Label in Name），语音控制说「详情」仍能命中。
          {...(label ? { 'aria-label': `${open ? '收起' : '详情'}：${label}` } : {})}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? '收起' : '详情'}
        </button>
      )}
    </div>
  );
}
